import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  extractWaveformFromUrl,
  formatVoiceDuration,
  generatePlaceholderWaveform,
} from '../../utils/supportVoice';

interface Props {
  src: string;
  durationSec?: number | null;
  /** user — исходящее в пузыре пользователя; admin — от поддержки */
  variant?: 'user' | 'admin';
  compact?: boolean;
  seed?: string;
}

const BAR_COUNT = 36;

/**
 * Плеер голосового сообщения в стиле Telegram / WhatsApp:
 * круглая кнопка play, волна, длительность, перемотка по клику на волну.
 */
const SupportVoicePlayer: React.FC<Props> = ({
  src,
  durationSec,
  variant = 'admin',
  compact = false,
  seed = src,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentSec, setCurrentSec] = useState(0);
  const [totalSec, setTotalSec] = useState(durationSec ?? 0);
  const [bars, setBars] = useState<number[]>(() => generatePlaceholderWaveform(seed, BAR_COUNT));
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBars(generatePlaceholderWaveform(seed, BAR_COUNT));
    extractWaveformFromUrl(src, BAR_COUNT).then((w) => {
      if (!cancelled) setBars(w);
    });
    return () => {
      cancelled = true;
    };
  }, [src, seed]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrentSec(audio.currentTime);
    const onMeta = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setTotalSec(Math.ceil(audio.duration));
      }
      setReady(true);
    };
    const onEnd = () => {
      setPlaying(false);
      setCurrentSec(0);
      audio.currentTime = 0;
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('durationchange', onMeta);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('durationchange', onMeta);
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, [src]);

  useEffect(() => {
    if (durationSec != null && durationSec > 0) {
      setTotalSec(durationSec);
    }
  }, [durationSec]);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      void audio.play().catch(() => {});
    }
  }, [playing]);

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      if (!audio || !totalSec) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      audio.currentTime = ratio * totalSec;
      setCurrentSec(audio.currentTime);
    },
    [totalSec],
  );

  const progress = totalSec > 0 ? Math.min(1, currentSec / totalSec) : 0;
  const displayDuration = playing || currentSec > 0
    ? formatVoiceDuration(currentSec)
    : formatVoiceDuration(totalSec);

  return (
    <div
      className={`sc-voice ${compact ? 'sc-voice--compact' : ''} sc-voice--${variant}`}
      role="group"
      aria-label="Голосовое сообщение"
    >
      <audio ref={audioRef} src={src} preload="metadata" />

      <button
        type="button"
        className={`sc-voice__play ${playing ? 'is-playing' : ''}`}
        onClick={togglePlay}
        aria-label={playing ? 'Пауза' : 'Воспроизвести'}
        disabled={!ready && !durationSec}
      >
        {playing ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <polygon points="8 5 20 12 8 19 8 5" />
          </svg>
        )}
      </button>

      <div
        className="sc-voice__wave"
        onClick={handleSeek}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          const rect = e.currentTarget.getBoundingClientRect();
          const fakeEvent = {
            currentTarget: e.currentTarget,
            clientX: rect.left + rect.width * 0.5,
          } as React.MouseEvent<HTMLDivElement>;
          handleSeek(fakeEvent);
        }}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={totalSec}
        aria-valuenow={Math.floor(currentSec)}
        aria-label="Прогресс воспроизведения"
        tabIndex={0}
      >
        {bars.map((h, i) => {
          const barProgress = (i + 0.5) / bars.length;
          const active = barProgress <= progress;
          return (
            <span
              key={i}
              className={`sc-voice__bar ${active ? 'is-active' : ''}`}
              style={{ '--bar-h': `${Math.round(h * 100)}%` } as React.CSSProperties}
            />
          );
        })}
      </div>

      <span className="sc-voice__time">{displayDuration}</span>
    </div>
  );
};

export default SupportVoicePlayer;
